// Note that when ignoredSentryErrors are strings, Sentry will try to partially match the messages
// If you need to achieve an exact match, use RegExp patterns instead.
export const ignoredSentryErrors: (string | RegExp)[] = [
  /* Why does this error happen?
   * This error means that ResizeObserver was not able to deliver all observations within a single animation frame.
   *
   * Why did we decide to ignore it?
   * It is most often benign (the app does not break, user sees no effects of it) and it's common practice to ignore this error in Sentry.
   *
   * Notes:
   * Keep in mind that "Seeing many of them would be worrysome, and might indicate an infinite resize loop." (not the case for us)
   * Checkout https://github.com/WICG/resize-observer/issues/38#issuecomment-422126006 for more details
   */
  'ResizeObserver loop limit exceeded',

  /* Why does this error happen?
   * This error (similarly as ResizeObserver loop limit exceeded)
   * means that a potential infinite loop in your rendering code was detected and stopped.
   * Technically this is a javascript error caused by a recursive loop within a resize observer.
   * When you resize your browser window manually, or this can also happen automatically when scrollbars
   * are added or when the page layout changes, then the front end rendering code needs to be informed
   * of that change so it can update the page. When the layout changes again while the frontend code is updating it,
   * then this error is generated.
   *
   * Why did we decide to ignore it?
   * It is most often benign (the app does not break, user sees no effects of it) and it's common practice to ignore this error in Sentry.
   *
   */
  'ResizeObserver loop completed with undelivered notifications.',

  /* Why does this error happen?
   * This error most probably happens when we unload the data - first we unload the components, so the references in the graph are referring to invalid components.
   * There might be also other scenarios.
   *
   * Why did we decide to ignore it?
   * We are not planning to fix this error soon, it's just a noise. User seems not to be disturbed by this error - it's not raised as a problem to our support team.
   * In the predictable future we will use different data model, which will resolve this issue permanently.
   */
  'Invalid graph model.',

  /* Why does this error happen?
   * This is quite tricky to reproduce, but from the comment it the code: "main issues are field updates triggered by internal field sync during merge.
   * The backbone models are not used during the merge flow. On closing the merge flow the scenario gets reloaded and all relevant backbone collections get reset."
   * There might be also other scenarios.
   *
   * Why did we decide to ignore it?
   * It seems irrelevant, no user complained about this. We’re planning on changing the way we handle the state anyway so this is just a noise.
   */
  'Model not found.',

  /* Why does this error happen?
   * User tries to save an object that was saved previously by someone else and the version is outdated.
   *
   * Why did we decide to ignore it?
   * We are aware that we need to implement the feature that allows merging object in such scenario or handle it in some other way.
   * However, we don't have the resources to work on it right now.
   * The same errors and metadata are saved on the backend, so we do not gain anything from duplicating the errors in Sentry.
   */
  /FetchError: The document \(id [a-f|\d]{24}\), has been modified by someone else\. Your version is \d+ and the database already holds version \d+\./,
  // The batch save endpoint (/api/batch/v2) reports the same optimistic-locking
  // conflict, but with a fixed message that has no document id or version
  // numbers, so the regex above does not match it. Same rationale.
  'The document has been modified by someone else. The database already holds a newer version.',

  /**
   * Why does this error happen?
   * It occurs when users lose their internet connection or similar events where there is no response from the server. The one notable exception is CORS errors which are also reported this way.
   *
   * Why did we decide to ignore it?
   * We ignore them since they are not really actionable from the details logged to Sentry. There are more details in the browser devtool console, but not in errors reported to Sentry. All the ignore strings cover the same type of error, they just have different messages in Firefox, Safari and Chrome.
   */
  'Failed to fetch',
  'TypeError: Failed to fetch',
  'TypeError: NetworkError when attempting to fetch resource.',
  'TypeError: Load failed',
  // Safari reports a failed worker chunk load (webpack importScripts) as
  // "NetworkError: Load failed" rather than the "TypeError: Load failed" above.
  'NetworkError: Load failed',
  // The Ace editor (advanced search query editor) spawns a web worker that
  // importScripts its worker file (e.g. worker-json.js) from the CDN. A
  // transient network failure, proxy, or blocking extension surfaces this as an
  // uncaught NetworkError. The editor keeps working (it just loses background
  // worker validation), and the failure is the same non-actionable network
  // class as the entries above.
  /Failed to execute 'importScripts' on 'WorkerGlobalScope': The script at '[^']+' failed to load\./,
  // Firefox-only code for an aborted network request, typically raised by
  // webpack's worker chunk loader when a user navigates away (e.g. clicking
  // through presentation slides) before an async chunk finishes loading. The
  // request was cancelled, not failed, so it is not actionable.
  'NS_BINDING_ABORTED',
  /**
   * Why does this error happen?
   * This is most likely happening due to the safe links being scanned by Outlook.
   * If you have an Office 365 subscription and your emails contain links to your site and these links use Safe Links feature then it is bound to happen.
   *
   * § https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-865857552
   * § https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
   *
   * Why did we decide to ignore it?
   * It is a false positive and we can't do anything about it.
   */
  'Non-Error promise rejection captured with value',
];
